import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Environment } from '../types';

interface EnvironmentContextType {
  environment: Environment;
  setEnvironment: (env: Environment) => void;
}

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(undefined);

const STORAGE_KEY = 'dev_portal_environment';

export const EnvironmentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Persist environment in localStorage so it survives page refresh
  const [environment, setEnvironmentState] = useState<Environment>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'production' || stored === 'sandbox' ? stored : 'sandbox';
  });

  const queryClient = useQueryClient();

  const setEnvironment = (env: Environment) => {
    if (env === environment) return;

    setEnvironmentState(env);
    localStorage.setItem(STORAGE_KEY, env);

    // Invalidate all queries that depend on environment so they re-fetch
    // with the correct sandbox_mode filter
    queryClient.invalidateQueries({ queryKey: ['applications'] });
    queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    queryClient.invalidateQueries({ queryKey: ['plans'] });
  };

  // Sync to localStorage on mount (in case it was set externally)
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, environment);
  }, []);

  return (
    <EnvironmentContext.Provider value={{ environment, setEnvironment }}>
      {children}
    </EnvironmentContext.Provider>
  );
};

export const useEnvironment = () => {
  const context = useContext(EnvironmentContext);
  if (context === undefined) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return context;
};
